import Link from 'next/link';

import React, { useMemo } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { GridTable, GridTableHeaderItem } from '@uikit';

import { MEMBERSHIP_TIER_ID } from '@api';

import TrophyIcon from '@uikit/icons/Trophy';

import { RewardPool, RewardPoolLeaderboardUser } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { nextProjectUrl } from '@shared/constants/urls';
import { formatPercent } from '@shared/helpers/number';
import {
  useRewardPoolLeaderboardInfinityQuery,
  useSelfRewardPoolLeaderboardDataQuery,
} from '@shared/hooks/query/rewardPools';

import InfiniteQueryState from '@store:web/components/common/QueryState/InfiniteQueryState';

const getFormattedPointsDistribution = ({
  userPoints,
  totalPoints,
}: {
  userPoints: number;
  totalPoints: number;
}) => {
  // in case of NaN show 0
  return formatPercent(userPoints / totalPoints || 0);
};

export const RewardPoolLeaderboardTableUser = ({
  user,
  totalPoints = 0,
  className,
  showRank,
  showDistribution,
}: {
  totalPoints?: number;
  user: RewardPoolLeaderboardUser;
  showRank?: boolean;
  showDistribution?: boolean;
  className?: string;
}) => {
  return (
    <Link
      target="_blank"
      href={nextProjectUrl.store(`/user/${user.name}`)}
      className={classNames(
        'min-h-12 flex items-center text-xs font-semibold lg:text-sm',
        className,
      )}
    >
      {showRank && (
        <span
          className={classNames('chip chip-md mr-4 min-w-12', {
            'chip-orange': user.rank === 1,
            'chip-blue': user.rank === 2,
            'chip-red': user.rank === 3,
          })}
        >
          {user.rank}
        </span>
      )}
      <div
        className={classNames(
          'avatar avatar-sm mr-2',
          {
            'avatar-border': user.membership !== MEMBERSHIP_TIER_ID.FREE,
          },
          {
            [MEMBERSHIP_TIER_ID.FREE]: '',
            [MEMBERSHIP_TIER_ID.PLUS]: 'border-membership-plus',
            [MEMBERSHIP_TIER_ID.PREMIUM]: 'border-membership-premium',
            [MEMBERSHIP_TIER_ID.PREMIUM_PLUS]: 'border-membership-premium-plus',
          }[user.membership],
        )}
      >
        <ImageProxy
          alt="Users's avatar"
          src={user.avatar}
          width={24}
          height={24}
          className="avatar-img"
        />
      </div>
      {user.displayedName || user.name || 'noname'}

      {!!user.reward && <TrophyIcon className="ml-2 icon-xs text-[#CF9011]" />}

      {showDistribution && (
        <span className="font-medium ml-auto">
          {getFormattedPointsDistribution({
            userPoints: user.points || 0,
            totalPoints: totalPoints,
          })}
        </span>
      )}
    </Link>
  );
};

interface RewardPoolLeaderboardTableProps {
  leaderboardQuery: ReturnType<typeof useRewardPoolLeaderboardInfinityQuery>;
  leaderboardMeQuery: ReturnType<typeof useSelfRewardPoolLeaderboardDataQuery>;
  rewardPool: RewardPool;
  showRankWithDistribution?: boolean;
}

/**
 * @deprecated
 * This component uses an outdated design. If you need to make any changes, consider moving to `LeaderboardNew` instead.
 * Currently, `LeaderboardNew` is used exclusively for pools with the distribution policy `RAFFLE_V2`.
 *
 * If you need to make changes for pools with other distribution policies,
 * it is recommended to migrate that functionality to use the new `LeaderboardNew` component as well.
 */
const RewardPoolLeaderboardTable = ({
  leaderboardQuery,
  showRankWithDistribution,
  leaderboardMeQuery,
}: RewardPoolLeaderboardTableProps) => {
  const { isAuth } = useAuth();
  const { data: leaderboard } = leaderboardQuery;

  const allUsers = useMemo(
    () => leaderboard?.pages.flatMap((page) => page.users) || [],
    [leaderboard],
  );

  const { data: leaderboardSelf } = leaderboardMeQuery;

  const header = useMemo(() => {
    const result: GridTableHeaderItem<
      RewardPoolLeaderboardUser,
      {
        totalPoints: number;
      }
    >[] = [
      {
        label: 'Member',
        name: 'member',
        render: ({ item }) => {
          return <RewardPoolLeaderboardTableUser showRank={showRankWithDistribution} user={item} />;
        },
      },
    ];

    if (showRankWithDistribution) {
      result.push({
        label: 'Distribution',
        name: 'distribution',
        cellClassName: 'flex justify-end',
        render: ({ item, meta }) => {
          return getFormattedPointsDistribution({
            userPoints: item.points || 0,
            totalPoints: meta?.totalPoints || 0,
          });
        },
      });
    }

    return result;
  }, [showRankWithDistribution]);

  const showLeaderboardSelf = isAuth && !!leaderboardSelf;

  const headRowClassName =
    'pb-2 font-medium text-[#65657B] border-b border-base-border-medium text-2xs leading-3 lg:text-xs';
  const rowBodyClassName = 'border-b min-h-12 items-center border-[#F3F3F7]';

  const totalPoints = leaderboard?.pages.length
    ? leaderboard.pages[leaderboard.pages.length - 1].points_total
    : 0;

  return (
    <>
      {showLeaderboardSelf && (
        <RewardPoolLeaderboardTableUser
          user={leaderboardSelf}
          showDistribution={showRankWithDistribution}
          showRank={showRankWithDistribution}
          totalPoints={totalPoints}
          className={classNames(rowBodyClassName, 'border-y mb-4 lg:mb-6')}
        />
      )}

      {!!allUsers.length && (
        <GridTable
          className="overflow-auto"
          header={header}
          meta={{
            totalPoints,
          }}
          rowClassName={classNames({
            'grid-cols-[1fr_80px]': showRankWithDistribution,
          })}
          bodyRowClassName={rowBodyClassName}
          headRowClassName={headRowClassName}
          data={allUsers}
        />
      )}

      <InfiniteQueryState
        isEmpty={!allUsers.length}
        query={leaderboardQuery}
        empty={
          <p className="h-[11.25rem] border-b border-[#F3F3F7] text-center flex justify-center items-center text-[#09090B] font-medium text-sm">
            No Results
          </p>
        }
        loading={
          <>
            {leaderboardQuery.isPending && <div className={headRowClassName}>Members</div>}
            <div className="flex flex-col">
              {Array.from({ length: 20 }, (_, i) => {
                return (
                  <div key={i} className={classNames(rowBodyClassName, 'flex')}>
                    {showRankWithDistribution && (
                      <span className="skeleton-block rounded-full w-12 mr-4 h-4" />
                    )}

                    <span className="avatar avatar-sm animate-skeleton-pulse mr-2" />

                    <span className="skeleton-block w-full max-w-32 h-4 rounded-full" />

                    {showRankWithDistribution && (
                      <span className="skeleton-block ml-auto w-12 rounded-full" />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        }
      />
    </>
  );
};

export default RewardPoolLeaderboardTable;
